import { Popover as BasePopover } from "@base-ui/react/popover";
import type { ReactElement, ReactNode } from "react";
import { cx } from "../../lib/cx";

export interface PopoverProps {
  align?: "start" | "center" | "end";
  children: ReactNode;
  /** Merged onto the popup with `cx`. */
  className?: string;
  onOpenChange?: (open: boolean) => void;
  /** Optional control; a popover is ephemeral, never a URL layer (D02). */
  open?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  /** A heading inside the popup, which also names it. */
  title?: ReactNode;
  /**
   * The button that opens it. Base UI merges its props (`aria-expanded`,
   * the click handler, the ref) into this element, so it must be a real
   * `<button>` at least 48 px square.
   */
  trigger: ReactElement;
}

/**
 * A non-modal popover over Base UI Popover: outside press and Escape close,
 * focus returns to the trigger.
 *
 * The popup carries `data-ephemeral` for T10's Android Back bridge, like
 * `Menu`: Back closes the popover before it touches history.
 */
export function Popover({
  trigger,
  children,
  title,
  side,
  align,
  open,
  onOpenChange,
  className,
}: PopoverProps) {
  return (
    <BasePopover.Root onOpenChange={onOpenChange} open={open}>
      <BasePopover.Trigger render={trigger} />
      <BasePopover.Portal>
        <BasePopover.Positioner
          align={align ?? "center"}
          className="z-[70]"
          collisionPadding={12}
          side={side ?? "bottom"}
          sideOffset={8}
        >
          <BasePopover.Popup
            className={cx(
              "a-card w-max max-w-[min(320px,calc(100vw-24px))] origin-(--transform-origin) p-4 text-base text-ink shadow-lift outline-none transition-[opacity,transform] duration-150 data-ending-style:scale-95 data-starting-style:scale-95 data-ending-style:opacity-0 data-starting-style:opacity-0",
              className
            )}
            data-ephemeral=""
          >
            {title ? (
              <BasePopover.Title className="mb-1 font-semibold text-base">
                {title}
              </BasePopover.Title>
            ) : null}
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
